/**
 * Drug-allergy and drug-condition checks for a patient's medications, from the annotated FDA
 * labels in drugLabels (functions/src/openfda/sync.js, label phases). The drug-drug half lives
 * in utils/drugInteractions.
 *
 * What each label carries for this, written by the sync:
 *   selfKeys                 what the drug IS: "substance:amoxicillin", "chem:D010406"
 *                            (Penicillins, MeSH), "class:penicillin class antibacterial"
 *   ciChemClasses            chemical classes MED-RT contraindicates it with (an allergy to
 *                            penicillins rules amoxicillin out)
 *   allergyMentions          what its hypersensitivity sentences name, with the sentence
 *   contraindicatedDiseases  MED-RT diseases with MeSH synonyms (normalized `terms`) and, when
 *                            the label spells it out, its own sentence
 *
 * Patient allergies and conditions are free text, so both are matched through `normalize`,
 * which must stay identical to the server's (functions/src/openfda/interactions.js) — the
 * parity test in src/tests/medicationSafety.test.js pins them together.
 */

import { SEVERITY_RANK } from "utils/drugInteractions";

/** Copy of the server's `words`: lowercase words, punctuation as spaces, crude plural strip. */
function words(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((w) => (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w));
}

export const normalize = (text) => words(text).join(" ");

// How patients name their allergies (AllergyForm's common-allergen chips among them), as the
// label keys they correspond to. Normalized wording on the left.
const ALLERGEN_ALIASES = {
  penicillin: ["chem:D010406"],
  "penicillin allergy": ["chem:D010406"],
  amoxicillin: ["substance:amoxicillin", "chem:D010406"],
  cephalosporin: ["chem:D002511"],
  "beta lactam": ["chem:D010406", "chem:D002511", "chem:D015780"],
  sulfa: ["chem:D013449"],
  "sulfa drug": ["chem:D013449"],
  sulfonamide: ["chem:D013449"],
  nsaid: ["class:nonsteroidal anti inflammatory drug"],
  aspirin: ["substance:aspirin"],
  codeine: ["substance:codeine"],
  morphine: ["substance:morphine"],
  opioid: ["class:opioid agonist"],
  opiate: ["class:opioid agonist"],
  statin: ["class:hmg coa reductase inhibitor"],
  macrolide: ["chem:D017963"],
  fluoroquinolone: ["chem:D024841"],
  quinolone: ["chem:D024841"],
  tetracycline: ["substance:tetracycline", "chem:D013752"],
  "iodine contrast": ["allergen:iodine"],
  "contrast dye": ["allergen:iodine"],
  iodine: ["allergen:iodine"],
  latex: ["allergen:latex"],
  peanut: ["allergen:peanut"],
  soy: ["allergen:soy"],
  egg: ["allergen:egg"],
  milk: ["allergen:milk"],
  dairy: ["allergen:milk"],
  fish: ["allergen:fish"],
  shellfish: ["allergen:shellfish"],
  wheat: ["allergen:wheat"],
  gluten: ["allergen:wheat"],
  sulfite: ["allergen:sulfite"],
  gelatin: ["allergen:gelatin"],
};

const allergenText = (allergy) =>
  typeof allergy === "string" ? allergy : allergy?.allergen || allergy?.name || "";

/**
 * The label keys an allergy covers, as a Map of key → how sure the link is:
 *   "named"   the patient named it, or the drug they named contains it / is in that
 *             chemical family (amoxicillin → penicillins)
 *   "family"  only the same pharmacologic class as a drug they named (ibuprofen → NSAIDs)
 * `allergyLabel` is the drugLabels document for an allergy saved from the drug search.
 */
export function allergenKeys(allergy, allergyLabel = null) {
  const keys = new Map();
  const add = (key, how) => {
    if (key && !(keys.get(key) === "named")) keys.set(key, how);
  };

  const text = normalize(allergenText(allergy)).replace(/ allergy$/, "");
  if (text) {
    (ALLERGEN_ALIASES[text] || []).forEach((k) => add(k, "named"));
    add(`substance:${text}`, "named");
    add(`class:${text}`, "named");
  }
  for (const key of allergyLabel?.selfKeys || []) {
    add(key, key.startsWith("class:") ? "family" : "named");
  }
  return keys;
}

/** A human name for a label key, from the label that carries it: "chem:D010406" → "Penicillins". */
function keyName(label, key) {
  const [kind, value] = [key.slice(0, key.indexOf(":")), key.slice(key.indexOf(":") + 1)];
  if (kind === "chem") {
    const c = [...(label?.chemClasses || []), ...(label?.ciChemClasses || [])].find(
      (x) => x.id === value
    );
    return c?.name || value;
  }
  if (kind === "class") {
    // "Nonsteroidal Anti-inflammatory Drug [EPC]" → "Nonsteroidal Anti-inflammatory Drug"
    const bare = (x) => String(x).replace(/\s*\[[^\]]*\]\s*$/, "");
    const c = [...(label?.classes || []), ...(label?.pharmClassEpc || [])].find(
      (x) => normalize(bare(x)) === value
    );
    return c ? bare(c) : value;
  }
  return value;
}

const pickWorst = (reasons) =>
  reasons.reduce((a, b) => (SEVERITY_RANK[b.severity] > SEVERITY_RANK[a.severity] ? b : a));

/**
 * Medications the patient has recorded an allergy against. `entries`: `[{ medication, label }]`;
 * `allergies`: `[{ allergy, label }]` (label only for allergies saved from the drug search).
 * One item per medication × allergy, worst reason first:
 *   `{ id, medication, allergy, severity, reasons: [{ kind, severity, matched, excerpt?,
 *   section? }] }`, kind being "ingredient" | "family" | "medrt" | "label".
 */
export function findAllergyConflicts(entries, allergies) {
  const out = [];
  for (const { medication, label } of entries || []) {
    if (!label?.selfKeys) continue;
    const self = new Set(label.selfKeys);
    const ciChem = new Map((label.ciChemClasses || []).map((c) => [`chem:${c.id}`, c.name]));
    const mentions = new Map((label.allergyMentions || []).map((m) => [m.key, m]));

    for (const { allergy, label: allergyLabel } of allergies || []) {
      const reasons = [];
      for (const [key, how] of allergenKeys(allergy, allergyLabel)) {
        if (self.has(key)) {
          reasons.push({
            kind: how === "family" ? "family" : "ingredient",
            severity: how === "family" ? "caution" : "avoid",
            matched: keyName(label, key),
            matchedKind: key.slice(0, key.indexOf(":")),
          });
        }
        if (ciChem.has(key)) {
          reasons.push({ kind: "medrt", severity: "avoid", matched: ciChem.get(key) });
        }
        if (mentions.has(key)) {
          const m = mentions.get(key);
          reasons.push({
            kind: "label",
            severity: m.severity,
            matched: m.name,
            excerpt: m.excerpt,
            section: m.section,
          });
        }
      }
      if (!reasons.length) continue;
      reasons.sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity]);
      out.push({
        id: `${medication.id || medication.name}|${allergenText(allergy)}`,
        medication,
        allergy,
        allergenName: allergenText(allergy),
        severity: pickWorst(reasons).severity,
        reasons,
        labelUrl: label.dailymedUrl || null,
      });
    }
  }
  return out.sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity]);
}

const conditionText = (c) => (typeof c === "string" ? c : c?.condition || c?.name || "");
const isCurrentCondition = (c) => typeof c === "string" || c?.status !== "resolved";

/** Every n-gram of the condition's words, so "Kidney failure (long-term renal failure)"
 *  contains "kidney failure" and "renal failure". */
function phraseSet(text) {
  const tokens = words(text);
  const out = new Set();
  for (let i = 0; i < tokens.length; i++) {
    let gram = "";
    for (let n = 0; n < 6 && i + n < tokens.length; n++) {
      gram = n ? `${gram} ${tokens[i + n]}` : tokens[i];
      out.add(gram);
    }
  }
  return out;
}

/**
 * Medications contraindicated in a condition the patient has recorded (resolved ones skipped).
 * `{ id, medication, condition, conditionName, disease, severity, excerpt?, section? }`, worst
 * first. With no label sentence to show, the MED-RT listing alone is graded "caution".
 */
export function findConditionConflicts(entries, conditions) {
  const current = (conditions || []).filter(isCurrentCondition).map((c) => ({
    condition: c,
    name: conditionText(c),
    phrases: phraseSet(conditionText(c)),
  }));
  // One item per medication × condition: "Asthma" can match both MED-RT's Asthma and its
  // Bronchial Hyperreactivity, and the patient needs to hear it once, at its worst.
  const out = new Map();
  for (const { medication, label } of entries || []) {
    for (const disease of label?.contraindicatedDiseases || []) {
      for (const c of current) {
        if (!(disease.terms || []).some((t) => c.phrases.has(t))) continue;
        const item = {
          id: `${medication.id || medication.name}|${c.name}`,
          medication,
          condition: c.condition,
          conditionName: c.name,
          disease: disease.name,
          severity: disease.excerpt ? disease.severity : "caution",
          excerpt: disease.excerpt || null,
          section: disease.section || null,
          labelUrl: label.dailymedUrl || null,
        };
        const prev = out.get(item.id);
        const better =
          !prev ||
          SEVERITY_RANK[item.severity] > SEVERITY_RANK[prev.severity] ||
          (item.severity === prev.severity && item.excerpt && !prev.excerpt);
        if (better) out.set(item.id, item);
      }
    }
  }
  return [...out.values()].sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity]);
}
